import React, { useEffect, useLayoutEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelectUserQuery } from "src/api/userAPI";
import { useSkuDetailQuery } from "src/api/skuAPI";
import { getPathValue } from "src/utils/queryStringUtil";
import cn from "classnames";
import Body from "../common/Body";
import Box from "../common/Box";
import Button from "../common/Button";
import styled from "styled-components";

const SkuDetail = () => {
  const navigate = useNavigate();
  const storeId: any = getPathValue();
  const [erpCode, setErpCode] = useState("");
  const [queryString, setQueryString] = useState({
    store_type: "",
    brand: "",
  });
  const [skuDetail, setSkuDetail] = useState<any>({
    py_data: {},
    items: [],
  });
  const [skuApiSkip, setSkuApiSkip] = useState(true);

  // API -> /sku/sku-result/{shopcode}?store_type={store_type}&brand={brand} 호출
  const storeInfo = useSelectUserQuery(storeId, { skip: storeId === undefined });
  const skuInfo = useSkuDetailQuery({ erpCode, queryString }, { skip: skuApiSkip });

  useEffect(() => {
    if (storeId !== undefined && storeInfo.isSuccess) {
      setErpCode(storeInfo.data.data.erp_code);
      setQueryString({
        store_type: storeInfo.data.data.store_type,
        brand: storeInfo.data.data.brand,
      });
      setSkuApiSkip(false);
    }
  }, [storeInfo]);

  useEffect(() => {
    if (skuInfo.isSuccess) {
      setSkuDetail(skuInfo.data.data);
    }
  }, [skuInfo]);

  const store_type = queryString.store_type;
  const pyData = skuDetail.py_data;
  const items = skuDetail.items;
  const { r_py, w_py, store_type_r_py, store_type_w_py } = pyData;

  const getDistribution = (type: string) => {
    // 유통망별 데이터 값
    // "A": 백화점, "B": 대리점, "C": 직영점, "ADF": 면세점
    // 백화점: "A", 대리점: "B", 직영점: "C", 아울렛: "B", 팩토리: "B", 면세점: "ADF"
    if (type === "A") {
      return "백화점";
    } else if (type === "B") {
      return "대리점";
    } else if (type === "C") {
      return "직영점";
    } else if (type === "ADF") {
      return "면세점";
    } else {
      return "유통망";
    }
  };

  const getSkuBox = (items: any, index: number) => {
    const getMyPercent = !isFinite(Math.round((items.real_stock / items.sku_stock) * 100)) ? 0 : Math.round((items.real_stock / items.sku_stock) * 100);
    const getTypePercent = !isFinite(Math.round((items.store_type_real_stock / items.store_type_sku_stock) * 100))
      ? 0
      : Math.round((items.store_type_real_stock / items.store_type_sku_stock) * 100);

    return (
      <li key={index}>
        <div className={cn("my-sku", { high: getMyPercent >= 100 && getMyPercent < 120 ? true : false }, { warning: getMyPercent >= 120 })}>
          <div className="title">{items.title}</div>
          <div className="sku-status">
            <p>
              <span>{getMyPercent}</span>%
            </p>
            <p>{getMyPercent >= 100 ? (getMyPercent >= 120 ? "warning" : "high") : "normal"}</p>
          </div>
          <div className="gauge-bar">
            <div>
              <span className="hidden">{getMyPercent >= 100 ? (getMyPercent >= 120 ? "warning" : "high") : "normal"}</span>
            </div>
          </div>
          <div className="sku-value">
            <span>{items.real_stock}</span> / <span>{items.sku_stock}</span>
          </div>
        </div>
        <div className="type-avg-sku">
          <div className="title">
            <span>{storeInfo.isSuccess && getDistribution(store_type)} 평균</span>
            <span>{getTypePercent >= 100 ? (getTypePercent >= 120 ? "warning" : "high") : "normal"}</span>
          </div>
          <div className="type-sku-status">
            <p>
              <span>{getTypePercent}</span>%
            </p>
            <p>
              <span>{items.store_type_real_stock}</span> / <span>{items.store_type_sku_stock}</span>
            </p>
          </div>
        </div>
      </li>
    );
  };

  return (
    <>
      <header>
        <div>
          <Button variant="text" className="btn-back" onClick={() => navigate(-1)}>
            SKU
          </Button>
        </div>
      </header>

      <Body>
        <TitleBox>
          <div>
            <p className="name">PY : 평수</p>
            <span>* 매장(창고)</span>
          </div>
          <dl>
            <div className="my-py">
              <dt>해당 매장</dt>
              <dd>
                <span>{r_py}</span>py (<span>{w_py}</span>py)
              </dd>
            </div>
            <div className="type-avg-py">
              <dt>{storeInfo.isSuccess && getDistribution(store_type)} 평균</dt>
              <dd>
                <span>{store_type_r_py}</span>py (<span>{store_type_w_py}</span>py)
              </dd>
            </div>
          </dl>
        </TitleBox>

        <Box pd="0">
          <TitleSet>
            <p>
              <span>SKU 현황</span>
              <span>* 재고 / 가용SKU</span>
            </p>
          </TitleSet>
          <SkuBox>{items.map(getSkuBox)}</SkuBox>
        </Box>
      </Body>
    </>
  );
};

const TitleBox = styled.div`
  padding: 20px 15px 0;
  > div {
    padding-bottom: 10px;
    margin-bottom: 20px;
    border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};
    ${({ theme }) => theme.flexBox.flexMixin("row", "space-between", "flex-end")}
    .name {
      font-size: 16px;
      font-weight: 700;
    }
    span {
      color: #818181;
      font-family: "Roboto";
      font-size: 12px;
      font-weight: 400;
    }
  }
  > dl {
    ${({ theme }) => theme.flexBox.flexMixin("row", "space-between", "center")}
    > div {
      ${({ theme }) => theme.flexBox.flexMixin("row", "flex-start", "center")}
      flex: 1 0 auto;
      vertical-align: middle;
      &.my-py {
        > dd {
          color: ${({ theme }) => theme.color.main};
        }
      }
      > dt {
        color: #818181;
        font-family: "Roboto";
        font-size: 14px;
        word-break: keep-all;
      }
      > dd {
        padding-left: 8px;
        font-family: "Poppins";
        font-size: 16px;
        font-weight: 500;
      }
    }
  }
`;

const TitleSet = styled.div`
  padding: 50px 15px 0;
  > p {
    ${({ theme }) => theme.flexBox.flexMixin("row", "space-between", "flex-end")}
    padding-bottom: 8px;
    border-bottom: 2.5px solid #000;
    > span {
      font-size: 16px;
      font-weight: 700;
      &:last-child {
        color: #818181;
        font-family: "Roboto";
        font-size: 12px;
        font-weight: 400;
      }
    }
  }
`;

const SkuBox = styled.ul`
  ${({ theme }) => theme.flexBox.flexMixin("row", "flex-start", "center", "wrap")}
  padding: 0 7px;
  > li {
    width: 50%;
    padding: 24px 8px 0;
    .my-sku {
      padding: 16px;
      border-radius: 4px;
      background: ${({ theme }) => theme.color.bgPrimary};
      .title {
        padding-bottom: 8px;
        border-bottom: 1px solid #fff;
        font-size: 16px;
        font-weight: 700;
        text-transform: uppercase;
      }
      .sku-status {
        > p {
          display: inline-block;
          color: ${({ theme }) => theme.color.secondary};
          font-size: 16px;
          > span {
            font-family: "Poppins";
            font-size: 32px;
            font-weight: 500;
            letter-spacing: -1px;
          }
          &:last-child {
            padding-left: 8px;
            color: #737d9b;
            font-size: 12px;
            text-transform: capitalize;
          }
        }
      }
      .gauge-bar {
        position: relative;
        width: 100%;
        height: 3px;
        margin-bottom: 8px;
        background-color: #fff;
        > div {
          width: 40%;
          height: 100%;
          background-color: ${({ theme }) => theme.color.lineSecondary};
        }
        &::before {
          content: "";
          position: absolute;
          left: 40%;
          top: 0;
          display: none;
          width: 30%;
          height: 3px;
          background-color: ${({ theme }) => theme.color.subMain};
        }
        &::after {
          content: "";
          position: absolute;
          left: 70%;
          top: 0;
          display: none;
          width: 30%;
          height: 3px;
          background-color: ${({ theme }) => theme.color.subWarning};
        }
      }
      .sku-value {
        color: ${({ theme }) => theme.color.secondary};
        font-family: "Poppins";
        font-size: 12px;
        letter-spacing: 0.32px;
        > span:first-child {
          color: #000;
        }
      }
      &.high {
        .sku-status {
          > p {
            color: ${({ theme }) => theme.color.subMain};
            &:last-child {
              color: ${({ theme }) => theme.color.secondary};
            }
          }
        }
        .gauge-bar {
          &::before {
            display: inline-block;
          }
        }
      }
      &.warning {
        .sku-status {
          > p {
            color: ${({ theme }) => theme.color.warning};
            > span:last-child {
              color: ${({ theme }) => theme.color.warning};
            }
          }
        }
        .gauge-bar {
          &::before {
            display: inline-block;
          }
          &::after {
            display: inline-block;
          }
        }
      }
    }
    .type-avg-sku {
      padding: 16px 16px 0;
      .title {
        ${({ theme }) => theme.flexBox.flexMixin()}
        padding-bottom: 4px;
        border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};
        > span {
          color: ${({ theme }) => theme.color.secondary};
          font-family: "Poppins";
          font-size: 12px;
          &:last-child {
            color: ${({ theme }) => theme.color.lineSecondary};
            text-transform: capitalize;
          }
        }
      }
      .type-sku-status {
        ${({ theme }) => theme.flexBox.flexMixin()}
        > p {
          color: ${({ theme }) => theme.color.secondary};
          font-family: "Poppins";
          font-size: 12px;
          &:first-child {
            > span {
              font-size: 18px;
            }
          }
          &:last-child {
            > span:first-child {
              color: #000;
            }
            letter-spacing: 0.32px;
          }
        }
      }
    }
  }
`;

export default SkuDetail;
